import type { ReactNode } from 'react';

import { Icon } from '#/components/Icon/Icon';
import { LayoutHeaderPanelRowContent } from '#/layouts/Layout/Header/Panel/LayoutHeaderPanelRowContent';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import type { HamburgerMenuExpandableRowType } from './data/types/HamburgerMenuData';
import {
  HamburgerMenuExpandableButton,
  HamburgerMenuRowChevronHolder,
  HamburgerMenuRowsHolder,
} from './HamburgerMenu.styled';
import { hamburgerMenuConstantsByRowType } from './hamburgerMenuConstants';

type Props = {
  type: HamburgerMenuExpandableRowType;
  title: string;
  trackingContext: string;
  expanded: boolean;
  onToggleExpanded: (type: HamburgerMenuExpandableRowType) => void;
  // Indents children past the icon slot, off for rows that put their own icon there
  nested?: boolean;
  children: ReactNode;
};

export function HamburgerMenuExpandableRow({
  type,
  title,
  trackingContext,
  expanded,
  onToggleExpanded,
  nested = true,
  children,
}: Props) {
  const { icon, qaId, trackingName } = hamburgerMenuConstantsByRowType[type];
  const childrenId = `hamburger-menu-${type}-rows`;

  const onClick = () => {
    // Expand only: collapsing isn't tracked
    if (!expanded) {
      trackEvent(`Expanded: ${trackingName}`, { context: trackingContext });
    }
    onToggleExpanded(type);
  };

  return (
    <li>
      <HamburgerMenuExpandableButton
        type="button"
        aria-expanded={expanded}
        aria-controls={childrenId}
        data-qa-id={qaId}
        onClick={onClick}
      >
        <LayoutHeaderPanelRowContent icon={icon} title={title} />
        <HamburgerMenuRowChevronHolder aria-hidden>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={12} />
        </HamburgerMenuRowChevronHolder>
      </HamburgerMenuExpandableButton>

      <HamburgerMenuRowsHolder
        id={childrenId}
        data-nested={nested || undefined}
        hidden={!expanded}
        data-qa-id={childrenId}
      >
        {children}
      </HamburgerMenuRowsHolder>
    </li>
  );
}
